import { useRouter } from 'next/router';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useProduct from 'hooks/use-product';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import { useDispensaryCategory } from 'hooks/use-dispensary-category';

import { GqlDispensaries as Dispensary, GqlBrand as Brand } from 'types/graphql';
import { AllSubcategories } from 'shared/constants';
import { isMedOnly, isRecOnly } from 'shared/core/helpers/dispensaries';
import { getBrand } from 'src/utils/get-brand';

type Subcategory = {
  key: string;
  label: string;
  value: string;
};

const getSubcategoryList = (subcategories: Subcategory[]): string => {
  let subcategoryList = '';
  subcategories.sort((a, b) => a.label.localeCompare(b.label));
  const truncatedSubcategories = subcategories.slice(0, 5);

  truncatedSubcategories.forEach((subcategory, index) => {
    if (index === truncatedSubcategories.length - 1) {
      subcategoryList += `and ${subcategory.label.toLowerCase()}`;
    } else {
      subcategoryList += `${subcategory.label.toLowerCase()}, `;
    }
  });

  return subcategoryList;
};

const getMenuTypes = (dispensary: Dispensary | undefined): string => {
  if (!dispensary) {
    return '';
  }

  if (isMedOnly(dispensary)) {
    return `medicinal`;
  }

  if (isRecOnly(dispensary)) {
    return `recreational`;
  }

  return `recreational and medicinal`;
};

const getBrandName = (brands: Brand[], brandId: string[] | string | undefined): string | undefined => {
  if (typeof brandId !== 'string') {
    return undefined;
  }

  return getBrand({ brands, brandId }).brand?.name;
};

type UseMetaInfoReturn = {
  dispensaryName?: string;
  dispensaryCity?: string;
  dispensaryState?: string;
  productName?: string;
  productBrand?: string;
  brandName?: string | false;
  productCategory?: string;
  productSubcategory?: string;
  productSubcategories?: string;
  menuTypes?: string;
};

export const useMetaInfo = (): UseMetaInfoReturn => {
  const {
    query: { brandId, subcategory },
  } = useRouter();
  const { dispensary } = useDispensary();
  const { product } = useProduct();
  const { brands, subcategories } = useMenuFilterOptions();
  const category = useDispensaryCategory();

  return {
    dispensaryName: dispensary?.name,
    dispensaryCity: dispensary?.location?.city,
    dispensaryState: dispensary?.location?.state,
    productName: product?.name,
    productBrand: product?.brandName ?? product?.brand?.name,
    brandName: getBrandName(brands, brandId),
    productCategory: category?.label,
    productSubcategory: AllSubcategories.find((sc) => sc.value === subcategory)?.label,
    productSubcategories: getSubcategoryList(subcategories as Subcategory[]),
    menuTypes: getMenuTypes(dispensary),
  };
};
